import { useAccessToken } from 'components/templates/AuthenticationProvider/AuthenticationProvider';
import React from 'react';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';
import { useEnablePushNotificationsMutation } from './__generated__/useEnablePushNotificationsMutation.graphql';
import { useEnablePushNotificationsQuery } from './__generated__/useEnablePushNotificationsQuery.graphql';

const pushNotificationFragment = graphql`
  fragment useEnablePushNotifications_notificationSubscriptions on NotificationSubscriptions {
    id
    surveyInviteContactChannels
  }
`;

const pushNotificationQuery = graphql`
  query useEnablePushNotificationsQuery {
    notificationSubscriptions {
      surveyInviteContactChannels
    }
  }
`;

const pushNotificationMutation = graphql`
  mutation useEnablePushNotificationsMutation($input: UpdateNotificationSubscriptions!) {
    updateNotificationContactChannelSubscriptions(input: $input) {
      message
      notificationSubscriptions {
        id
        surveyInviteContactChannels
      }
    }
  }
`;

export function useEnablePushNotifications() {
  const getAccessToken = useAccessToken();

  const data = useLazyLoadQuery<useEnablePushNotificationsQuery>(pushNotificationQuery, {});

  const [updateNotificationContactChannelSubscriptions] =
    useMutation<useEnablePushNotificationsMutation>(pushNotificationMutation);

  return React.useCallback(async () => {
    try {
      const permission = await Notification.requestPermission();

      if (permission === 'granted') {
        // 1. Send fcm token to back end
        // getAccessToken will call fetchNewTokens, in which we will get the FCM token and send it to the backend
        await getAccessToken('notificationsChanged');

        // 2. Enable push notifications on the user account
        const existingChannels = data.notificationSubscriptions.surveyInviteContactChannels ?? [];
        const updatedChannels = existingChannels.includes('pushNotification')
          ? existingChannels
          : [...existingChannels, 'pushNotification'];

        updateNotificationContactChannelSubscriptions({
          variables: {
            input: {
              surveyInviteContactChannels: updatedChannels,
            },
          },
          // onCompleted: (response) => {
          // },
          onError: (err) => {
            console.error('Survey subscription update error:', err);
          },
        });
      }
    } catch (error) {
      // We could log this to get stats on how many users are accepting push notifications
      console.error('Failed to request notification permission:', error);
    }
  }, [
    updateNotificationContactChannelSubscriptions,
    data.notificationSubscriptions.surveyInviteContactChannels,
    getAccessToken,
  ]);
}
